import React from 'react';
import _ from 'underscore';

import { ButtonType, ButtonWidget } from '@tableau/widgets';

import { AdminAnalysisTable } from './AdminAnalysisTable';
import { BrowserTitleService } from '../../commonInfra/services/BrowserTitleService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IFeatureFlagsDoNotUse } from '../../ts/ServerApi/types/IFeatureFlagsDoNotUse';
import { Host } from '../types/Host';
import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { ServerUrls } from '../../app/ServerUrls';
import { ServiceLocator } from '../../app/ServiceLocator';
import { WorkerProcessStatus } from './WorkerProcessStatus';
import { WorkerProcessType } from '../types/WorkerProcess';
import { WorkerStatusType } from '../types/GetWorkerProcessStatus';

const legendStatuses = [
  WorkerStatusType.active,
  WorkerStatusType.busy,
  WorkerStatusType.passive,
  WorkerStatusType.unlicensed,
  WorkerStatusType.down,
  WorkerStatusType.external,
  WorkerStatusType.statusNotAvailable,
];

const WorkerProcessTypes = [
  WorkerProcessType.clusterController, // cluster controller should be first
  WorkerProcessType.gateway,
  WorkerProcessType.vizportal,
  WorkerProcessType.wgServer,
  WorkerProcessType.vizqlServer,
  WorkerProcessType.cacheServer,
  WorkerProcessType.searchServer,
  WorkerProcessType.backgrounder,
  WorkerProcessType.dataServer,
  WorkerProcessType.dataEngine, // TFS 646124 - Remove the feature flag, remove the dataengine.
  WorkerProcessType.hyper,
  WorkerProcessType.fileStore,
  WorkerProcessType.repository,
  WorkerProcessType.flowProcessor,
  WorkerProcessType.metrics
];

export interface ServerStatusState {
    components: string[];
    refreshButtonBusy: boolean;
}

export interface ServerStatusProps {
    hosts: Host[];
    triggerRefresh: () => Promise<void>;
}


export const messagesMap = Messages as { [key: string]: ((...args: any[]) => string) | undefined };


export function fetchLocalisedValue(component: string): string {
    const componentFn = messagesMap[component];
    return componentFn ? componentFn(): null;
}

export class ServerStatus extends React.PureComponent<ServerStatusProps,ServerStatusState> {
    private wgEnabled: boolean;
    private flowProcessorEnabled: boolean;
    private featureFlagsDoNotUse: IFeatureFlagsDoNotUse;
    private showAdminViews: boolean;

    constructor(props: ServerStatusProps) {
        super(props);
        this.state ={
            components: [],
            refreshButtonBusy: false
        };
        BrowserTitleService.setTitleWithTranslatedSpace({translatedSpaceName: Messages.serverStatus()});
        this.showAdminViews= !this.server.isAdminViewsDisabled();
        this.featureFlagsDoNotUse =  this.server.featureFlagsDoNotUse();
        this.wgEnabled =  ServerSettingsUnauthenticated.isWgEnabled();
        this.flowProcessorEnabled = this.server.flowProcessorEnabled();
    }

    public componentDidMount() {
        this.setState({components: this.getComponents()});
    }

    private showMultinodePrimaryWorkerLabels = () => {
        return this.props.hosts.length > 1 && !FeatureFlags.tableauServicesManagerEnabled;
    }

    private server: SessionManagementService = getSessionManagementServiceInstance();

    private getComponents = (): string[] => {
        return _.without(WorkerProcessTypes,
            ((this.featureFlagsDoNotUse && !this.featureFlagsDoNotUse.ha2Enabled) || (this.featureFlagsDoNotUse && !this.featureFlagsDoNotUse.ha2FilestoreEnabled) ? WorkerProcessType.fileStore : null),
            ((this.featureFlagsDoNotUse && !this.featureFlagsDoNotUse.ha2Enabled) || this.props.hosts.length === 1 ? WorkerProcessType.clusterController : null),
            (!this.wgEnabled ? WorkerProcessType.wgServer : null),
            // TFS 646124 - Remove the feature flag, remove this check.
            (FeatureFlags.hyperOnServerEnabled ? WorkerProcessType.dataEngine : WorkerProcessType.hyper),
            // Tableau Prep Server Flow Processor
            (!this.flowProcessorEnabled ? WorkerProcessType.flowProcessor : null)
          );
      }

    private isPreferred = (component: string, host: Host): boolean => {
        return component === WorkerProcessType.repository
          && this.featureFlagsDoNotUse.ha2Enabled
          && host.processes
          && host.processes[WorkerProcessType.repository]
          && host.processes[WorkerProcessType.repository][0].preferred;
      }

    private refreshStatuses = () => {
        this.setState({refreshButtonBusy: true});
        this.props.triggerRefresh().finally(() => this.setState({refreshButtonBusy: false}));
    };

    private hideStatsForLoadTimes  = () => {
        return ServerSettingsUnauthenticated.hideAdminVizStatsForLoadTimes();
    };

    private hidePerformanceofViews  = () => {
        return ServerSettingsUnauthenticated.hideAdminVizPerformanceofViews();
    };

    private hidePerformanceofFlowRuns  = () => {
        return this.server.hideAdminVizPerformanceofFlowRuns();
    };

    private analysisUrl = (sheetUrlId: string) => {
        return ServerUrls.getInstance().serverStatusVizUrl(sheetUrlId);
    };

    private renderHostHeader = (): JSX.Element => {
       return (
            <tr key="serverStatusRow">
                <th className={'tb-data-grid-headers-line'+ this.showMultinodePrimaryWorkerLabels() && ' tb-data-grid-headers-line-multiline'}>
                    <div className="tb-data-grid-header-text">
                        {Messages.process()}
                    </div>
                </th>
                {this.props.hosts && this.props.hosts.map((host, index) => {
                    return (
                    <th className={'tb-data-grid-headers-line'+ this.showMultinodePrimaryWorkerLabels() && ' tb-data-grid-headers-line-multiline'} key={'header'+host.name+index}>
                        {(index === 0 && this.showMultinodePrimaryWorkerLabels()) &&
                        <div className="tb-data-grid-header-text">
                            {Messages.primary()}
                        </div>
                        }
                        {(index > 0 && this.showMultinodePrimaryWorkerLabels()) &&
                        <div className="tb-data-grid-header-text">
                            {Messages.worker() + index}
                        </div>
                        }
                        <div className="tb-data-grid-header-text">
                            {host.name}
                        </div>
                    </th>);
                })
                    }
            </tr>
       );
    }

    private renderAnalysisTable = (): JSX.Element => {
        return(
        <div className="tb-settings-section">
            <div className="tb-settings-group-name">
                {Messages.analysisTableHeaderAnalysis()}
            </div>
            <div>
                {
                    <AdminAnalysisTable
                        inServerArea={true}
                        hideStatsForLoadTimes={this.hideStatsForLoadTimes()}
                        hidePerformanceofViews={this.hidePerformanceofViews()}
                        hidePerformanceofFlowRuns={this.hidePerformanceofFlowRuns()}
                        analysisUrl={this.analysisUrl}
                    />
                }
            </div>
        </div>);
    }

    private renderComponentRows = (): JSX.Element => {
        return (
            <>
             {this.state.components && this.state.components.map((component, index) => {
                 return (
                <tr key={component+index}>
                    <td className="tb-data-grid-separator-row">
                        <span className="tb-data-grid-cell-text tb-lr-padded-wide">
                            { fetchLocalisedValue(component)}
                        </span>
                    </td>
                    {
                    this.props.hosts && this.props.hosts.map((host, index) => {
                    return <td className="tb-data-grid-separator-row"  key = {host.name + index}>
                            <div className="tb-lr-padded-wide tb-worker-statuses-container">
                                {host.processes[component] && host.processes[component].map((process, index) => {
                                    return <WorkerProcessStatus
                                        key={process.port+index}
                                        process={process}
                                        isPreferred={this.isPreferred(component, host)}
                                    />;
                                })}
                            </div>
                        </td>;
                    })
                    }

                </tr>
             );})}
            </>
        );
    }

    private renderLegendStatuses = (): JSX.Element => {
        return (
        <div className="tb-status-legend tb-padded-box">
            {legendStatuses.map((status, index) => {
                return <span className="tb-status-legend-item" key={'legend'+status+index}>
                    <span className={'tb-icon-process-status tb-icon-process-status-'+status}/>
                    <span className="tb-status-legend-item-label">
                        {fetchLocalisedValue('workerStatus_'+status)}
                    </span>
                </span>;
            })}
        </div>);
    }


    public render(){
        return (
        <>
        <div className="tb-tabs-banner">
            <span className="tb-tabs-banner-title">
                <span className="tb-label">
                    {Messages.serverStatus()}
                </span>
                <span className="tb-count"> &nbsp; </span>
            </span>
        </div>
        <div className="tb-tabs-banner-subplace">
            <div className="tb-scroll tb-scroll-settings">
                <div className="tb-settings-container">
                    <div className="tb-server-status-grid-container tb-settings-section">
                        <div className="tb-settings-group-name">
                            {Messages.processStatus()}
                        </div>
                        <div className="tb-settings-msg">
                            {Messages.processStatusDetails()}
                        </div>
                        <table className="tb-static-grid-table tb-static-grid-table-settings-width">
                            <tbody>
                                {this.renderHostHeader()}
                                {this.renderComponentRows()}
                            </tbody>
                        </table>
                        <div className="tb-status-controls-container">
                            <span className="tb-padded-box">
                                <ButtonWidget
                                    disabled={ this.state.refreshButtonBusy}
                                    testId={'refresh-statuses'}
                                    buttonType={ ButtonType.Outline }
                                    handleClick={ () => this.refreshStatuses() }>
                                {this.state.refreshButtonBusy && <span className = "tb-icon-spinner tb-lr-padded-narrow"/> }
                                <span>{ Messages.refreshStatus() }</span>
                                </ButtonWidget>
                            </span>
                            {this.renderLegendStatuses()}
                        </div>
                    </div>
                    {this.showAdminViews && this.renderAnalysisTable()}
                </div>
            </div>
        </div>
        </>
        );
    }
}